import React, { useCallback, useEffect, useMemo, useState } from 'react';
import WizardShell from '../ui/wizard-shell';
import { WizardCard, WizardKVGrid, WizardKVItem, WizardPill, WizardPills, WizardSection, WizardYesNoBadge } from '../ui/wizard-layout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { formatDate } from '../../lib/dateFormat';
import { useStandort } from '../layouts/use-standort';
import CommunicationComposer, { type RecipientGroup, type GroupDraftState } from '../communications/CommunicationComposer';
import { getRooms, getRoomsUnits, type Room, type RoomUnit } from '../../lib/rooms';
import { unterrichtseinheitenApi, unitIdOf } from '../../lib/unterrichtseinheiten';
import { courseRescheduleApi } from '../../lib/courseReschedule';
import { scheduleConflictsApi, type ConflictCheckResult } from '../../lib/scheduleConflicts';
import { api, historyTemplatesApi } from '../../lib/api';
import { cn } from '../../lib/utils';

const DAYS_OF_WEEK = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

function sameDraftState(a?: GroupDraftState, b?: GroupDraftState): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    !!a.selectedChannels?.email === !!b.selectedChannels?.email &&
    !!a.selectedChannels?.sms === !!b.selectedChannels?.sms &&
    (a.email?.subjectTpl || '') === (b.email?.subjectTpl || '') &&
    (a.email?.htmlTpl || '') === (b.email?.htmlTpl || '') &&
    (a.email?.textTpl || '') === (b.email?.textTpl || '') &&
    (a.sms?.smsTpl || '') === (b.sms?.smsTpl || '')
  );
}

function sameDraftMap(a: Record<string, GroupDraftState>, b: Record<string, GroupDraftState>): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!sameDraftState(a[key], b[key])) return false;
  }
  return true;
}

export interface CourseRescheduleSeed {
  id: string;
  courseId?: number | null;
  ruleId?: number | null;
  name: string;
  fachName?: string | null;
  studentName: string;
  studentEmail: string;
  teacherId: string;
  roomId?: number | null;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  startDate: string;
  sourceType?: string;
  participants?: Array<{ subprofileId: string; wpUserId: number; subIndex: number; name: string }> | null;
  /** When set, the wizard was triggered via drag-and-drop. */
  dropTarget?: {
    date: string;       // YYYY-MM-DD (reference date; may be overridden by Wechsel-Datum)
    dayOfWeek: number;  // 1-7
    startTime: string;  // HH:MM
    endTime: string;    // HH:MM
  } | null;
  /** When true, the drop came from the Stundenplanansicht and a Wechsel-Datum step is shown. */
  fromTimetable?: boolean;
}

export interface CourseRescheduleWizardModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  course: CourseRescheduleSeed;
  onDone?: () => void;
}

/**
 * Check if two time ranges overlap (HH:MM format).
 */
function overlaps(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  const toMin = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  const a0 = toMin(aStart), a1 = toMin(aEnd);
  const b0 = toMin(bStart), b1 = toMin(bEnd);
  return a0 < b1 && b0 < a1;
}

const CourseRescheduleWizardModal: React.FC<CourseRescheduleWizardModalProps> = ({ open, onOpenChange, course, onDone }) => {
  const { selectedStandortId } = useStandort();
  const hasDropTarget = !!course.dropTarget;
  const fromTimetable = !!course.fromTimetable;

  // Steps vary by trigger:
  //   Calendar D&D:   Verschiebung → Raum → Kommunikation → Übersicht  (4 steps)
  //   Timetable D&D:  Verschiebung → Wechsel-Datum → Raum → Kommunikation → Übersicht  (5 steps)
  //   Right-click:    Verschiebung → Neuer Termin → Raum → Kommunikation → Übersicht  (5 steps)
  const stepLabels = useMemo(() => {
    const labels = [{ label: 'Verschiebung' }];
    if (hasDropTarget && fromTimetable) {
      labels.push({ label: 'Wechsel-Datum' });
    } else if (!hasDropTarget) {
      labels.push({ label: 'Neuer Termin' });
    }
    labels.push({ label: 'Raum' }, { label: 'Kommunikation' }, { label: 'Übersicht' });
    return labels;
  }, [hasDropTarget, fromTimetable]);
  const totalSteps = stepLabels.length;

  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step 1 — Verschiebung
  const [mode, setMode] = useState<'einmalig' | 'dauerhaft'>('einmalig');
  const [historyText, setHistoryText] = useState('');

  // Step 2 — Neuer Termin (only when no dropTarget) / Wechsel-Datum (timetable D&D)
  const [newDayOfWeek, setNewDayOfWeek] = useState(1);
  const [newStartTime, setNewStartTime] = useState('');
  const [newEndTime, setNewEndTime] = useState('');
  const [newDate, setNewDate] = useState('');
  const [wechselDatum, setWechselDatum] = useState('');

  // Step 3 — Raum
  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomUnitsForDate, setRoomUnitsForDate] = useState<RoomUnit[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [teacherRoomIds, setTeacherRoomIds] = useState<Set<number>>(new Set());
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [conflictCheck, setConflictCheck] = useState<ConflictCheckResult | null>(null);
  const [conflictLoading, setConflictLoading] = useState(false);

  // Step 4 — Communication drafts
  const [communicationDrafts, setCommunicationDrafts] = useState<Record<string, GroupDraftState>>({});

  const standortId = useMemo(() => {
    const n = typeof selectedStandortId === 'string' ? Number.parseInt(selectedStandortId, 10) : (selectedStandortId ?? 0);
    return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
  }, [selectedStandortId]);

  // Derived: the effective new date/time (either from dropTarget or manual input)
  const effectiveNewDayOfWeek = hasDropTarget ? course.dropTarget?.dayOfWeek ?? newDayOfWeek : newDayOfWeek;
  const effectiveNewStartTime = hasDropTarget ? course.dropTarget?.startTime ?? newStartTime : newStartTime;
  const effectiveNewEndTime = hasDropTarget ? course.dropTarget?.endTime ?? newEndTime : newEndTime;
  // For timetable D&D, the user-chosen Wechsel-Datum overrides the reference date from the drop.
  const effectiveNewDate = hasDropTarget
    ? (fromTimetable ? wechselDatum : (course.dropTarget?.date ?? newDate))
    : newDate;

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setIsSubmitting(false);
    setMode('einmalig');
    setHistoryText('');
    setNewDayOfWeek(course.dayOfWeek);
    setNewStartTime(course.startTime);
    setNewEndTime(course.endTime);
    setNewDate(new Date().toISOString().split('T')[0]);
    setWechselDatum('');
    setSelectedRoomId(null);
    setRooms([]);
    setRoomUnitsForDate([]);
    setTeacherRoomIds(new Set());
    setCommunicationDrafts({});
    setConflictCheck(null);
    setConflictLoading(false);
  }, [open, course]);

  // Map step number to logical step name
  const logicalStep = useMemo(() => {
    if (step === 1) return 'verschiebung';
    if (hasDropTarget && fromTimetable) {
      // Timetable D&D: 5 steps — verschiebung → wechsel_datum → raum → kommunikation → uebersicht
      if (step === 2) return 'wechsel_datum';
      if (step === 3) return 'raum';
      if (step === 4) return 'kommunikation';
      return 'uebersicht';
    }
    if (!hasDropTarget) {
      // Right-click: 5 steps — verschiebung → neuer_termin → raum → kommunikation → uebersicht
      if (step === 2) return 'neuer_termin';
      if (step === 3) return 'raum';
      if (step === 4) return 'kommunikation';
      return 'uebersicht';
    }
    // Calendar D&D: 4 steps — verschiebung → raum → kommunikation → uebersicht
    if (step === 2) return 'raum';
    if (step === 3) return 'kommunikation';
    return 'uebersicht';
  }, [step, hasDropTarget, fromTimetable]);

  // Load rooms when entering Raum step
  useEffect(() => {
    if (!open || logicalStep !== 'raum') return;
    if (!effectiveNewDate || !effectiveNewStartTime || standortId <= 0) return;

    let cancelled = false;
    setRoomsLoading(true);

    const loadRooms = async () => {
      try {
        const allRooms = await getRooms(standortId);
        if (cancelled) return;
        const activeRooms = allRooms.filter(r => r.aktiv !== false && r.status !== 'inactive' && r.status !== 'draft');
        setRooms(activeRooms);

        const ids = activeRooms.map(r => r.id);
        let units: RoomUnit[] = [];
        if (ids.length > 0) {
          units = await getRoomsUnits(ids, effectiveNewDate, effectiveNewDate);
          if (cancelled) return;
          setRoomUnitsForDate(units);
        }

        // Load teacher's units for that day to determine which rooms teacher uses
        const teacherUnits = await unterrichtseinheitenApi.list({
          teacherId: course.teacherId,
          start: effectiveNewDate,
          end: effectiveNewDate,
        });
        if (cancelled) return;
        const tRoomIds = new Set<number>();
        teacherUnits.forEach(u => {
          const rid = Number(u.raum_id ?? 0);
          if (Number.isFinite(rid) && rid > 0) tRoomIds.add(rid);
        });
        setTeacherRoomIds(tRoomIds);

        // Auto-suggest room
        const isRoomFreeAt = (roomId: number) => {
          return !units
            .filter(u => u.raum_id === roomId)
            .some(u => overlaps(effectiveNewStartTime, effectiveNewEndTime, u.start_zeit.slice(11, 16), u.end_zeit.slice(11, 16)));
        };

        // Try course's current room first
        const currentRoomId = course.roomId ? Number(course.roomId) : null;
        if (currentRoomId && activeRooms.some(r => r.id === currentRoomId) && isRoomFreeAt(currentRoomId)) {
          setSelectedRoomId(currentRoomId);
          return;
        }

        // Try teacher's rooms on that day
        for (const rid of tRoomIds) {
          if (activeRooms.some(r => r.id === rid) && isRoomFreeAt(rid)) {
            setSelectedRoomId(rid);
            return;
          }
        }
      } catch (e) {
        console.error('Failed to load rooms', e);
      } finally {
        if (!cancelled) setRoomsLoading(false);
      }
    };

    loadRooms();
    return () => { cancelled = true; };
  }, [open, logicalStep, effectiveNewDate, effectiveNewStartTime, effectiveNewEndTime, standortId, course.teacherId, course.roomId]);

  // Live conflict check for current candidate slot
  useEffect(() => {
    if (!open) return;
    if (!effectiveNewDate || !effectiveNewStartTime || !effectiveNewEndTime) return;
    const teacherId = Number(course.teacherId || 0);
    if (!(teacherId > 0)) return;

    let cancelled = false;
    const run = async () => {
      setConflictLoading(true);
      try {
        const result = await scheduleConflictsApi.check({
          teacherId,
          roomId: selectedRoomId,
          date: effectiveNewDate,
          startTime: effectiveNewStartTime,
          endTime: effectiveNewEndTime,
          excludeUnitId: unitIdOf(course.id),
          excludeCourseId: course.courseId ? Number(course.courseId) : null,
          excludeDate: course.startDate || null,
        });
        if (!cancelled) setConflictCheck(result);
      } catch {
        if (!cancelled) setConflictCheck(null);
      } finally {
        if (!cancelled) setConflictLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, effectiveNewDate, effectiveNewStartTime, effectiveNewEndTime, selectedRoomId, course.teacherId, course.id, course.courseId, course.startDate]);

  // Auto-generated history text
  const effectiveHistoryText = useMemo(() => {
    if (historyText.trim()) return historyText;
    const modeLabel = mode === 'einmalig' ? 'einmalig' : 'dauerhaft';
    return `Kurs "${course.name}" wurde ${modeLabel} verschoben.\nVon: ${DAYS_OF_WEEK[course.dayOfWeek - 1]}, ${course.startTime} – ${course.endTime} Uhr\nAuf: ${DAYS_OF_WEEK[effectiveNewDayOfWeek - 1]}, ${effectiveNewStartTime} – ${effectiveNewEndTime} Uhr`;
  }, [historyText, mode, course.name, course.dayOfWeek, course.startTime, course.endTime, effectiveNewDayOfWeek, effectiveNewStartTime, effectiveNewEndTime]);

  useEffect(() => {
    let cancelled = false;
    const loadHistoryTemplate = async () => {
      if (!open || historyText.trim()) return;
      try {
        const rendered = await historyTemplatesApi.render({
          template_key: 'course.rescheduled.history',
          standort_id: standortId || undefined,
          vars: {
            courseName: course.name || '',
            modeLabel: mode === 'einmalig' ? 'einmalig' : 'dauerhaft',
            newDate: effectiveNewDate,
            newStartTime: effectiveNewStartTime,
            newEndTime: effectiveNewEndTime,
          },
        });
        if (cancelled) return;
        if (rendered.history) setHistoryText(rendered.history);
      } catch {
        // fallback remains effectiveHistoryText
      }
    };
    void loadHistoryTemplate();
    return () => {
      cancelled = true;
    };
  }, [open, historyText, course.name, mode, effectiveNewDate, effectiveNewStartTime, effectiveNewEndTime, standortId]);

  const newDateFormatted = useMemo(() => {
    if (!effectiveNewDate) return '–';
    try { return formatDate(effectiveNewDate); } catch { return effectiveNewDate; }
  }, [effectiveNewDate]);

  const selectedRoomName = useMemo(() => {
    if (!selectedRoomId) return '–';
    return rooms.find(r => r.id === selectedRoomId)?.name ?? `Raum #${selectedRoomId}`;
  }, [selectedRoomId, rooms]);
  const isConflictFree = (conflictCheck?.hasConflict ?? false) === false;

  // Communication composer groups
  const composerGroups: RecipientGroup[] = useMemo(() => {
    const groups: RecipientGroup[] = [];
    const sharedVars: Record<string, string> = {
      studentName: course.studentName,
      courseName: course.name,
      oldDayOfWeek: DAYS_OF_WEEK[course.dayOfWeek - 1] || '',
      oldStartTime: course.startTime,
      oldEndTime: course.endTime,
      newDayOfWeek: DAYS_OF_WEEK[effectiveNewDayOfWeek - 1] || '',
      newStartTime: effectiveNewStartTime,
      newEndTime: effectiveNewEndTime,
      startDate: newDateFormatted,
      roomName: selectedRoomName,
    };

    const studentRecipients = (course.participants || []).map(p => ({
      id: `person:${p.wpUserId}:${p.subIndex}`,
      type: 'person' as const,
      wpUserId: p.wpUserId,
      subIndex: p.subIndex,
      name: p.name,
    }));
    if (studentRecipients.length > 0) {
      groups.push({
        key: 'student',
        label: 'Teilnehmer',
        recipients: studentRecipients,
        templateKeys: { email: 'course.rescheduled.student' },
        vars: sharedVars,
        defaultSelectedChannels: { email: true, sms: false },
      });
    }

    const tid = Number(course.teacherId);
    if (tid > 0) {
      groups.push({
        key: 'teacher',
        label: 'Lehrer',
        recipients: [{
          id: `person:${tid}:`,
          type: 'person' as const,
          wpUserId: tid,
          subIndex: null,
          name: `Lehrer #${tid}`,
        }],
        templateKeys: { email: 'course.rescheduled.teacher' },
        vars: sharedVars,
        defaultSelectedChannels: { email: true, sms: false },
      });
    }

    return groups;
  }, [course, effectiveNewDayOfWeek, effectiveNewStartTime, effectiveNewEndTime, newDateFormatted, selectedRoomName]);

  // Validation per step
  const canContinueStep2 = useMemo(
    () => !!(newStartTime && newEndTime && newDate && newEndTime > newStartTime),
    [newStartTime, newEndTime, newDate],
  );

  const canNext = useMemo(() => {
    if (logicalStep === 'verschiebung') return true;
    if (logicalStep === 'neuer_termin') return canContinueStep2;
    if (logicalStep === 'wechsel_datum') return !!wechselDatum;
    if (logicalStep === 'raum') {
      return !!selectedRoomId && !conflictLoading && !conflictCheck?.hasBlockingConflict;
    }
    return true;
  }, [logicalStep, canContinueStep2, wechselDatum, selectedRoomId, conflictLoading, conflictCheck]);

  const handleNext = () => { if (step < totalSteps) setStep(step + 1); };
  const handleBack = () => { if (step > 1) setStep(step - 1); };

  // Channel states from drafts for summary
  const channelEmailStudent = !!communicationDrafts['student']?.selectedChannels?.email;
  const channelEmailTeacher = !!communicationDrafts['teacher']?.selectedChannels?.email;

  const handleDraftsChange = useCallback(({ groups: next }: { groups: Record<string, GroupDraftState> }) => {
    setCommunicationDrafts((prev) => (sameDraftMap(prev, next) ? prev : next));
  }, []);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const courseId = course.courseId ? Number(course.courseId) : 0;
      // Stundenplan-Einheiten heißen „unit-123“; ohne Präfix-Abzug wurde die Einheit
      // nicht verschoben, sondern daneben eine neue angelegt.
      const unitId = unitIdOf(course.id);

      // 1. Reschedule via backend
      await courseRescheduleApi.reschedule({
        mode,
        courseId,
        unitId,
        instanceDate: course.startDate,
        newDate: effectiveNewDate,
        newStartTime: effectiveNewStartTime,
        newEndTime: effectiveNewEndTime,
        newRoomId: selectedRoomId,
        historyText: effectiveHistoryText,
        historyTemplateKey: 'course.rescheduled.history',
        courseName: course.name,
      });

      // 2. Send communications via existing /api/communications/send
      const failedChannels: string[] = [];
      if (standortId > 0) {
        for (const [groupKey, draft] of Object.entries(communicationDrafts)) {
          if (!draft) continue;
          const group = composerGroups.find(g => g.key === groupKey);
          if (!group) continue;

          if (draft.selectedChannels?.email && draft.email?.subjectTpl && draft.email?.htmlTpl) {
            try {
              await api.post('/api/communications/send', {
                channel: 'email',
                standortId,
                recipients: group.recipients,
                templateKey: group.templateKeys?.email || '',
                draft: {
                  subjectTpl: draft.email.subjectTpl,
                  htmlTpl: draft.email.htmlTpl,
                  textTpl: draft.email.textTpl || '',
                },
                vars: group.vars,
              });
            } catch (emailErr) {
              console.error('Email send failed for group', groupKey, emailErr);
              failedChannels.push(`${group.label} (E-Mail)`);
            }
          }

          if (draft.selectedChannels?.sms && draft.sms?.smsTpl) {
            try {
              await api.post('/api/communications/send', {
                channel: 'sms',
                standortId,
                recipients: group.recipients,
                templateKey: group.templateKeys?.sms || '',
                draft: { smsTpl: draft.sms.smsTpl },
                vars: group.vars,
              });
            } catch (smsErr) {
              console.error('SMS send failed for group', groupKey, smsErr);
              failedChannels.push(`${group.label} (SMS)`);
            }
          }
        }
      }

      if (failedChannels.length > 0) {
        toast.warning(
          `Kurs wurde verschoben, aber der Versand ist teilweise fehlgeschlagen: ${failedChannels.join(', ')}`,
        );
      } else {
        toast.success(`Kurs "${course.name}" wurde erfolgreich verschoben`);
      }
      onDone?.();
      onOpenChange(false);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Verschieben fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Kurs verschieben"
      description={`${course.name} – ${course.studentName}`}
      currentStep={step}
      steps={stepLabels}
      onBack={step > 1 ? handleBack : undefined}
      canBack={step > 1 && !isSubmitting}
      onNext={step < totalSteps ? handleNext : undefined}
      canNext={canNext}
      onPrimary={step === totalSteps ? handleConfirm : undefined}
      primaryLabel="Verschieben"
      canPrimary={!isSubmitting && !conflictLoading && !conflictCheck?.hasBlockingConflict}
      isLoading={isSubmitting}
    >
      <WizardCard className="py-4">
        {/* Step 1: Verschiebung */}
        {logicalStep === 'verschiebung' && (
          <>
            <WizardSection title="Verschiebungstyp">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setMode('einmalig')}
                  className={cn(
                    'flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors',
                    mode === 'einmalig'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-muted bg-background text-muted-foreground hover:bg-muted/30'
                  )}
                >
                  Einmalig
                  <div className="mt-1 text-xs font-normal opacity-70">Nur dieser eine Termin wird verschoben</div>
                </button>
                <button
                  type="button"
                  onClick={() => setMode('dauerhaft')}
                  className={cn(
                    'flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors',
                    mode === 'dauerhaft'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-muted bg-background text-muted-foreground hover:bg-muted/30'
                  )}
                >
                  Dauerhaft
                  <div className="mt-1 text-xs font-normal opacity-70">Alle zukünftigen Termine werden verschoben</div>
                </button>
              </div>
            </WizardSection>

            <WizardSection title="Terminänderung">
              <WizardKVGrid>
                <WizardKVItem label="Bisheriger Termin">
                  {DAYS_OF_WEEK[course.dayOfWeek - 1]}, {course.startTime} – {course.endTime} Uhr
                </WizardKVItem>
                <WizardKVItem label="Neuer Termin">
                  {hasDropTarget ? (
                    <>
                      {DAYS_OF_WEEK[effectiveNewDayOfWeek - 1]}, {effectiveNewStartTime} – {effectiveNewEndTime} Uhr
                      {fromTimetable
                        ? <span className="text-muted-foreground ml-1">(Datum wird in Schritt 2 festgelegt)</span>
                        : <> ({newDateFormatted})</>
                      }
                    </>
                  ) : effectiveNewDate ? (
                    <>{DAYS_OF_WEEK[effectiveNewDayOfWeek - 1]}, {effectiveNewStartTime} – {effectiveNewEndTime} Uhr ({newDateFormatted})</>
                  ) : (
                    <span className="text-muted-foreground">Wird in Schritt 2 festgelegt</span>
                  )}
                </WizardKVItem>
              </WizardKVGrid>
            </WizardSection>

            <WizardSection title="Historieneintrag (optional)">
              <Textarea
                value={historyText}
                onChange={(e) => setHistoryText(e.target.value)}
                placeholder={effectiveHistoryText}
                rows={3}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Wird in der Historie des Kurses gespeichert. Leer lassen für automatischen Text.
              </p>
            </WizardSection>
          </>
        )}

        {/* Step 2: Neuer Termin (only if no dropTarget) */}
        {logicalStep === 'neuer_termin' && (
          <>
            <WizardSection title="Aktueller Termin">
              <div className="text-sm text-muted-foreground">
                {DAYS_OF_WEEK[course.dayOfWeek - 1]}, {course.startTime} – {course.endTime} Uhr
              </div>
            </WizardSection>

            <WizardSection title="Neuer Termin">
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label>Wochentag</Label>
                  <Select value={String(newDayOfWeek)} onValueChange={(v) => setNewDayOfWeek(Number.parseInt(v, 10))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map((d, i) => (
                        <SelectItem key={i} value={String(i + 1)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Startzeit</Label>
                    <Input type="time" value={newStartTime} onChange={(e) => setNewStartTime(e.target.value)} />
                  </div>
                  <div className="grid gap-2">
                    <Label>Endzeit</Label>
                    <Input type="time" value={newEndTime} onChange={(e) => setNewEndTime(e.target.value)} />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label>Datum</Label>
                  <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
                </div>
              </div>
            </WizardSection>
          </>
        )}

        {/* Step 2b: Wechsel-Datum (only for timetable D&D) */}
        {logicalStep === 'wechsel_datum' && (
          <>
            <WizardSection title="Neuer Termin (aus Stundenplan)">
              <div className="text-sm text-muted-foreground">
                {DAYS_OF_WEEK[effectiveNewDayOfWeek - 1]}, {effectiveNewStartTime} – {effectiveNewEndTime} Uhr
              </div>
            </WizardSection>

            <WizardSection title={mode === 'einmalig' ? 'Für welches Datum?' : 'Ab welchem Datum?'}>
              <p className="text-xs text-muted-foreground mb-3">
                {mode === 'einmalig'
                  ? 'Wählen Sie das konkrete Datum, an dem der Kurs einmalig zu dieser Zeit stattfinden soll.'
                  : 'Wählen Sie das Datum, ab dem der Kurs dauerhaft zum neuen Zeitpunkt wechselt.'}
              </p>
              <div className="grid gap-2 max-w-xs">
                <Label>Wechsel-Datum</Label>
                <Input type="date" value={wechselDatum} onChange={(e) => setWechselDatum(e.target.value)} />
              </div>
            </WizardSection>
          </>
        )}

        {/* Step 3: Raum */}
        {logicalStep === 'raum' && (
          <WizardSection title="Raum auswählen">
            {conflictLoading ? (
              <div className="mb-3 text-xs text-muted-foreground">Konflikte werden geprüft…</div>
            ) : null}
            {conflictCheck?.hasConflict ? (
              <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3">
                <div className="text-sm font-medium text-red-800">Konflikt erkannt</div>
                <ul className="mt-1 list-disc pl-4 text-xs text-red-700 space-y-1">
                  {conflictCheck.conflicts.map((c) => (
                    <li key={`${c.type}-${c.message}`}>{c.message}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {roomsLoading ? (
              <div className="text-sm text-muted-foreground">Räume werden geladen...</div>
            ) : rooms.length === 0 ? (
              <div className="text-sm text-muted-foreground">Keine aktiven Räume für diesen Standort gefunden.</div>
            ) : (
              <>
                <div className="text-xs text-muted-foreground mb-4">
                  Wählen Sie einen Raum für den neuen Termin am {newDateFormatted}, {effectiveNewStartTime} – {effectiveNewEndTime} Uhr.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto pr-1">
                  {rooms.map(room => {
                    const units = roomUnitsForDate.filter(u => u.raum_id === room.id);
                    const isRoomFree = !units.some(u =>
                      overlaps(effectiveNewStartTime, effectiveNewEndTime, u.start_zeit.slice(11, 16), u.end_zeit.slice(11, 16))
                    );
                    const teacherIsHere = teacherRoomIds.has(room.id);

                    return (
                      <button
                        key={room.id}
                        type="button"
                        onClick={() => setSelectedRoomId(room.id)}
                        className={cn(
                          'rounded-xl border bg-background p-4 text-left transition-colors hover:bg-muted/30',
                          selectedRoomId === room.id ? 'border-primary ring-2 ring-primary/20' : 'border-muted'
                        )}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{room.name}</div>
                          {room.address ? <div className="mt-1 text-xs text-muted-foreground">{room.address}</div> : null}
                          <div className="mt-2">
                            <WizardPills className="gap-1.5">
                              <WizardPill
                                active={isRoomFree}
                                className={cn(isRoomFree ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white')}
                              >
                                {isRoomFree ? 'frei' : 'belegt'}
                              </WizardPill>
                              {isRoomFree && teacherIsHere ? (
                                <WizardPill active={false} className="bg-indigo-50 text-indigo-700 border border-indigo-200">
                                  Lehrer ist heute hier
                                </WizardPill>
                              ) : null}
                              {room.tags && room.tags.length > 0 ? (
                                <>
                                  {room.tags.slice(0, 3).map(t => (
                                    <WizardPill key={t} active={false} className="bg-muted text-muted-foreground">{t}</WizardPill>
                                  ))}
                                </>
                              ) : null}
                            </WizardPills>
                          </div>
                        </div>
                        {!isRoomFree ? (
                          <div className="mt-3 text-xs text-red-600">Raum ist zur gewählten Zeit belegt</div>
                        ) : (
                          <div className="mt-3 text-xs text-muted-foreground">Raum ist zur gewählten Zeit frei.</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </WizardSection>
        )}

        {/* Step 4: Kommunikation */}
        {logicalStep === 'kommunikation' && (
          <WizardSection title="Benachrichtigung">
            {composerGroups.length > 0 ? (
              <CommunicationComposer
                active={open && logicalStep === 'kommunikation'}
                standortId={standortId}
                groups={composerGroups}
                onDraftsChange={handleDraftsChange}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Keine Empfänger verfügbar.</p>
            )}
          </WizardSection>
        )}

        {/* Step 5: Übersicht */}
        {logicalStep === 'uebersicht' && (
          <>
            <WizardSection title="Terminänderung">
              <WizardKVGrid>
                <WizardKVItem label="Kurs">{course.name}</WizardKVItem>
                <WizardKVItem label="Teilnehmer">{course.studentName}</WizardKVItem>
                <WizardKVItem label="Verschiebungstyp">
                  <span className={cn('inline-block rounded px-2 py-0.5 text-xs font-medium', mode === 'einmalig' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800')}>
                    {mode === 'einmalig' ? 'Einmalig' : 'Dauerhaft'}
                  </span>
                </WizardKVItem>
                <WizardKVItem label="Bisheriger Termin">
                  {DAYS_OF_WEEK[course.dayOfWeek - 1]}, {course.startTime} – {course.endTime} Uhr
                </WizardKVItem>
                <WizardKVItem label="Neuer Termin">
                  {DAYS_OF_WEEK[effectiveNewDayOfWeek - 1]}, {effectiveNewStartTime} – {effectiveNewEndTime} Uhr ({newDateFormatted})
                </WizardKVItem>
                <WizardKVItem label="Raum">{selectedRoomName}</WizardKVItem>
                <WizardKVItem label="Konfliktfrei">
                  <WizardYesNoBadge yes={isConflictFree} />
                </WizardKVItem>
              </WizardKVGrid>
              {conflictCheck?.hasConflict ? (
                <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  {conflictCheck.conflicts.map((c) => (
                    <div key={`${c.type}-summary-${c.message}`}>- {c.message}</div>
                  ))}
                </div>
              ) : null}
            </WizardSection>

            <WizardSection title="Benachrichtigungen">
              <WizardKVGrid>
                <WizardKVItem label="Teilnehmer E-Mail"><WizardYesNoBadge yes={channelEmailStudent} /></WizardKVItem>
                <WizardKVItem label="Lehrer E-Mail"><WizardYesNoBadge yes={channelEmailTeacher} /></WizardKVItem>
              </WizardKVGrid>
            </WizardSection>

            <WizardSection title="Historieneintrag">
              <div className="whitespace-pre-wrap text-sm text-muted-foreground">{effectiveHistoryText}</div>
            </WizardSection>
          </>
        )}
      </WizardCard>
    </WizardShell>
  );
};

export default CourseRescheduleWizardModal;
